import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, ListChecks } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../config/settings-api';
import { slugifyFieldKey } from '../../utils/orderConfigHelpers';
import './Settings.css';
import './OrderSettings.css';

const serializeFields = (fields) =>
  JSON.stringify(
    (fields || []).map((field) => ({
      key: field.key,
      label: field.label,
      required: !!field.required,
    }))
  );

const CustomFieldsSettings = ({ fields = [], canManage, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewField, setShowNewField] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    const normalized = (fields || []).map((field, index) => ({
      key: field.key || slugifyFieldKey(field.label),
      label: field.label || '',
      required: field.required === true,
      position: field.position ?? index,
    }));
    setRows(normalized);
    setInitialSnapshot(serializeFields(normalized));
  }, [fields]);

  const isChanged = serializeFields(rows) !== initialSnapshot;

  const handleAddField = () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Enter a field label');
      return;
    }
    const key = slugifyFieldKey(label);
    if (rows.some((row) => row.key === key)) {
      toast.error('A field with this name already exists');
      return;
    }
    setRows((prev) => [...prev, { key, label, required: false }]);
    setNewLabel('');
    setShowNewField(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = rows.map((row, index) => ({
        key: row.key || slugifyFieldKey(row.label),
        label: row.label.trim(),
        required: !!row.required,
        position: index,
      })).filter((row) => row.label);

      const result = await settingsAPI.updateOrderCustomFields(payload);
      if (result.success) {
        toast.success('Custom fields saved');
        setInitialSnapshot(serializeFields(payload));
        onSaved?.(payload);
      } else {
        toast.error(result.msg || result.error || 'Failed to save custom fields');
      }
    } catch {
      toast.error('Failed to save custom fields');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-card order-settings-card">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <ListChecks size={20} />
          <div>
            <h2>Custom Order Fields</h2>
            <p>Add fields like shipment number for custom orders</p>
          </div>
        </div>
        {canManage && (
          <button type="button" className="settings-save-btn" onClick={handleSave} disabled={!isChanged || saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="order-settings-body">
        {rows.length === 0 && !showNewField && (
          <p className="order-settings-empty">No custom fields yet. Add shipment number or other fields.</p>
        )}

        <div className="order-custom-fields-list">
          {rows.map((row, index) => (
            <div key={row.key || index} className="order-custom-field-row">
              <div className="order-custom-field-main">
                <strong>{row.label}</strong>
                <span className="order-custom-field-key">{row.key}</span>
              </div>
              <label className="order-settings-toggle">
                <input
                  type="checkbox"
                  checked={row.required}
                  disabled={!canManage || saving}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, required: checked } : item)));
                  }}
                />
                Required
              </label>
              {canManage && (
                <button
                  type="button"
                  className="order-settings-icon-btn danger"
                  disabled={saving}
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                  title="Delete field"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div className={`order-settings-add-row ${rows.length === 0 ? 'no-margin' : ''}`}>
            {showNewField ? (
              <div className="order-settings-new-field">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Shipment Number"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddField()}
                />
                <button type="button" className="settings-inline-btn" onClick={handleAddField} disabled={saving}>Add</button>
                <button type="button" className="settings-inline-btn muted" onClick={() => { setShowNewField(false); setNewLabel(''); }} disabled={saving}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="settings-inline-btn" onClick={() => setShowNewField(true)} disabled={saving}>
                <Plus size={16} /> Add custom field
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomFieldsSettings;
